import { useCallback, useMemo } from 'react';
import { Edge, Node } from '@xyflow/react';

import { ForLoopConnectionValidator } from '../services/ForLoopConnectionValidator';
import {
  ConnectionValidationError,
  ConnectionValidationResult,
  IConnectionCompatibilityRule,
  INodeInputConfiguration,
  INodeOutputConfiguration,
  NodeConnectionTypes,
  NodeTypeCategory,
} from '../types/connections';
import { INodeType, INodeTypeDescription } from '../types/node-interface';

import { useWorkflow } from './useWorkflow';

export function useConnectionValidation(nodes: Node[], edges: Edge[]) {
  const { getNodeConfig } = useWorkflow();

  // Memoize compatibility rules (static data, never changes)
  const compatibilityRules = useMemo<IConnectionCompatibilityRule[]>(
    () => [
      {
        sourceConnectionType: NodeConnectionTypes.Main,
        targetConnectionType: NodeConnectionTypes.Main,
        isCompatible: true,
      },
    ],
    []
  );

  const getNodeTypeByName = useCallback(
    (typeName: string): INodeType | null => {
      return getNodeConfig(typeName) || null;
    },
    [getNodeConfig]
  );

  const getOutputConfiguration = useCallback(
    (nodeDesc: INodeTypeDescription): INodeOutputConfiguration | null => {
      const output = nodeDesc.output;
      if (!output) return null;

      // For nodes with no output
      if (!output.type) return null;

      return output;
    },
    []
  );

  const getInputConfiguration = useCallback(
    (nodeDesc: INodeTypeDescription): INodeInputConfiguration | null => {
      const input = nodeDesc.input;
      if (!input) return null;

      // For trigger nodes with no input (maxConnections: 0)
      if (!input.type || input.maxConnections === 0) return null;

      return input;
    },
    []
  );

  const validateConnectionTypes = useCallback(
    (
      sourceNodeDesc: INodeTypeDescription,
      targetNodeDesc: INodeTypeDescription,
      sourceHandle?: string,
      targetHandle?: string
    ): ConnectionValidationResult => {
      const sourceOutput = getOutputConfiguration(sourceNodeDesc, sourceHandle);
      const targetInput = getInputConfiguration(targetNodeDesc, targetHandle);

      if (!sourceOutput || !targetInput) {
        return {
          isValid: false,
          error: ConnectionValidationError.INCOMPATIBLE_TYPES,
          suggestion: 'Check that both nodes have compatible input/output configurations',
        };
      }

      const compatibility = compatibilityRules.find(
        rule =>
          rule.sourceConnectionType === sourceOutput.type &&
          rule.targetConnectionType === targetInput.type
      );

      if (!compatibility || !compatibility.isCompatible) {
        return {
          isValid: false,
          error: ConnectionValidationError.INCOMPATIBLE_TYPES,
          suggestion: `${sourceOutput.type} output is not compatible with ${targetInput.type} input`,
        };
      }

      if (compatibility.requiresAdapter) {
        return {
          isValid: true,
          warning: 'Connection requires data type adapter',
          suggestion: 'Data will be automatically converted between connection types',
        };
      }

      return { isValid: true };
    },
    [compatibilityRules, getOutputConfiguration, getInputConfiguration]
  );

  const validateConnectionCounts = useCallback(
    (
      sourceNode: Node,
      targetNode: Node,
      sourceNodeDesc: INodeTypeDescription,
      targetNodeDesc: INodeTypeDescription,
      sourceHandle?: string,
      targetHandle?: string
    ): ConnectionValidationResult => {
      // Check if target node is a trigger (triggers can't have inputs)
      if (targetNodeDesc.category === NodeTypeCategory.TRIGGER) {
        return {
          isValid: false,
          error: ConnectionValidationError.TRIGGER_AS_TARGET,
          suggestion: 'Trigger nodes cannot have input connections',
        };
      }

      // Get source output configuration
      const sourceOutput = getOutputConfiguration(sourceNodeDesc, sourceHandle);
      if (sourceOutput && sourceOutput.maxConnections !== undefined) {
        const currentOutputConnections = edges.filter(
          edge =>
            edge.source === sourceNode.id &&
            (edge.sourceHandle === sourceHandle || (!sourceHandle && !edge.sourceHandle))
        ).length;

        if (currentOutputConnections >= sourceOutput.maxConnections) {
          return {
            isValid: false,
            error: ConnectionValidationError.MAX_CONNECTIONS_EXCEEDED,
            suggestion: `Source node output can only have ${sourceOutput.maxConnections} connection(s)`,
          };
        }
      }

      // Get target input configuration
      const targetInput = getInputConfiguration(targetNodeDesc, targetHandle);
      if (targetInput && targetInput.maxConnections !== undefined) {
        const currentInputConnections = edges.filter(
          edge =>
            edge.target === targetNode.id &&
            (edge.targetHandle === targetHandle || (!targetHandle && !edge.targetHandle))
        ).length;

        if (currentInputConnections >= targetInput.maxConnections) {
          return {
            isValid: false,
            error: ConnectionValidationError.MAX_CONNECTIONS_EXCEEDED,
            suggestion: `Target node input can only have ${targetInput.maxConnections} connection(s)`,
          };
        }
      }

      return { isValid: true };
    },
    [edges, getOutputConfiguration, getInputConfiguration]
  );

  const validateConnection = useCallback(
    (
      sourceNode: Node,
      targetNode: Node,
      sourceHandle?: string,
      targetHandle?: string
    ): ConnectionValidationResult => {
      // Check if this is a ForLoop-related connection
      const isForLoopRelated =
        sourceNode.type === 'for_loop' ||
        targetNode.type === 'for_loop' ||
        sourceNode.parentId ||
        targetNode.parentId;

      // Validate ForLoop connections first if applicable
      if (isForLoopRelated) {
        const forLoopValidation = ForLoopConnectionValidator.validateConnection(
          sourceNode.id,
          targetNode.id,
          nodes,
          edges
        );

        if (!forLoopValidation.isValid || !forLoopValidation.canConnect) {
          return {
            isValid: false,
            error: forLoopValidation.error || forLoopValidation.reason,
            suggestion: forLoopValidation.reason,
          };
        }
      }

      const sourceNodeType = getNodeTypeByName(sourceNode.type || '');
      const targetNodeType = getNodeTypeByName(targetNode.type || '');

      if (!sourceNodeType || !targetNodeType) {
        return {
          isValid: false,
          error: 'Unknown node type',
          suggestion: 'Ensure both nodes are properly registered',
        };
      }

      // Validate target node allows source node type (unidirectional validation)
      const targetInput = getInputConfiguration(targetNodeType.description, targetHandle);
      if (targetInput?.filter?.allowedNodeTypes) {
        const allowedTypeConfig = targetInput.filter.allowedNodeTypes.find(
          config => config.type === sourceNode.type
        );

        if (!allowedTypeConfig) {
          return {
            isValid: false,
            error: ConnectionValidationError.FILTERED_CONNECTION,
            suggestion: `${targetNodeType.description.displayName} does not accept connections from ${sourceNodeType.description.displayName}`,
          };
        }

        // Validate per-node-type maxConnections
        const existingConnectionsFromType = edges.filter(
          edge =>
            edge.target === targetNode.id &&
            (edge.targetHandle === targetHandle || (!targetHandle && !edge.targetHandle)) &&
            nodes.find(n => n.id === edge.source)?.type === sourceNode.type
        ).length;

        if (existingConnectionsFromType >= allowedTypeConfig.maxConnections) {
          return {
            isValid: false,
            error: ConnectionValidationError.MAX_CONNECTIONS_EXCEEDED,
            suggestion: `${targetNodeType.description.displayName} can only accept ${allowedTypeConfig.maxConnections} connection(s) from ${sourceNodeType.description.displayName}`,
          };
        }
      }

      const connectionTypeValidation = validateConnectionTypes(
        sourceNodeType.description,
        targetNodeType.description,
        sourceHandle,
        targetHandle
      );

      if (!connectionTypeValidation.isValid) {
        return connectionTypeValidation;
      }

      const connectionCountValidation = validateConnectionCounts(
        sourceNode,
        targetNode,
        sourceNodeType.description,
        targetNodeType.description,
        sourceHandle,
        targetHandle
      );

      if (!connectionCountValidation.isValid) {
        return connectionCountValidation;
      }

      return connectionTypeValidation;
    },
    [
      getNodeTypeByName,
      getInputConfiguration,
      validateConnectionTypes,
      validateConnectionCounts,
      nodes,
      edges,
    ]
  );

  return {
    validateConnection,
  };
}
